import { useEffect, useMemo, useReducer } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '../../components/ui/Spinner';
import type {
  ReportingBreakdownFilters,
  ReportingFilters,
} from '../../types/reporting';
import { getReportingBreakdown } from './api';
import { isStatKey, type BreakdownColumn, type BreakdownStatKey } from './breakdownColumns';
import {
  renderBreakdownAttributeCell,
  renderBreakdownDimCell,
  renderBreakdownStatCell,
  StatusDotLegend,
  type MoneyDisplay,
} from './breakdownCell';
import type { RollupSort, RollupSortKey } from './breakdownGrouping';
import { childRequestFor } from './treeChildRequest';
import { toTreeRows, type TreeRowData } from './treeRows';
import { EMPTY_EXPANSION, expansionReducer } from './treeExpansion';

// Shared props threaded down the recursion (everything except the node itself).
interface TreeContext {
  filters: ReportingFilters;
  sort: RollupSort;
  visibleCols: BreakdownColumn[];
  expanded: ReadonlySet<string>;
  toggle: (key: string) => void;
  /** The page's display currency, threaded to every money cell. Absent = native. */
  money?: MoneyDisplay;
}

// Build the scoped /breakdown filters for a node's children (page filters + the
// parent-id scope), or null when the node is a leaf / un-scopable.
function childFiltersFor(
  node: { dim: TreeRowData['dim']; id: string | null },
  filters: ReportingFilters,
): ReportingBreakdownFilters | null {
  const req = childRequestFor(node);
  if (!req) return null;
  return { ...filters, groupBy: [req.groupBy], ...req.scope };
}

// One tree node (recursive). Renders its own row; when expanded, lazily fetches +
// renders its children indented beneath (spinner while in flight, coverage message
// when the scoped fetch returns nothing).
function TreeRow({
  node,
  depth,
  nodeKey,
  ctx,
}: {
  node: TreeRowData;
  depth: number;
  nodeKey: string;
  ctx: TreeContext;
}) {
  const childFilters = useMemo(
    () => childFiltersFor(node, ctx.filters),
    [node, ctx.filters],
  );
  const hasCaret = childFilters !== null;
  const isExpanded = ctx.expanded.has(nodeKey);

  const q = useQuery({
    queryKey: ['reporting-breakdown-child', childFilters],
    queryFn: () => getReportingBreakdown(childFilters!),
    enabled: isExpanded && childFilters !== null,
    placeholderData: (prev) => prev,
  });

  const childDim = childFilters?.groupBy[0];
  const children = useMemo(
    () => (q.data && childDim ? toTreeRows(q.data.rows, childDim, ctx.sort) : []),
    [q.data, childDim, ctx.sort],
  );

  const colCount = 1 + ctx.visibleCols.length;
  const rowTone = depth === 0 ? 'bg-slate-50/60 font-medium text-slate-800' : 'text-slate-700';
  const loading = isExpanded && q.isLoading;
  const empty = isExpanded && !q.isLoading && !q.isError && children.length === 0;

  return (
    <>
      <tr className={`border-t border-slate-100 ${rowTone} hover:bg-slate-50/70`}>
        <td className="whitespace-nowrap px-3 py-2 align-top">
          <span className="inline-flex items-center gap-1" style={{ paddingLeft: depth * 18 }}>
            {hasCaret ? (
              <button
                type="button"
                onClick={() => ctx.toggle(nodeKey)}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
                className="w-4 text-[10px] text-slate-400 hover:text-slate-700"
              >
                {isExpanded ? '▾' : '▸'}
              </button>
            ) : (
              <span className="inline-block w-4" aria-hidden />
            )}
            {renderBreakdownDimCell(node)}
          </span>
        </td>
        {ctx.visibleCols.map((c) =>
          isStatKey(c.key) ? (
            <td key={c.key} className="px-3 py-2 text-right align-top tabular-nums">
              {renderBreakdownStatCell(node, c.key, ctx.money)}
            </td>
          ) : (
            <td key={c.key} className="px-3 py-2 text-left align-top">
              {renderBreakdownAttributeCell(node, c)}
            </td>
          ),
        )}
      </tr>

      {loading && (
        <tr className="border-t border-slate-100">
          <td colSpan={colCount} className="px-3 py-2">
            <span className="inline-flex items-center gap-2 text-xs text-slate-400" style={{ paddingLeft: (depth + 1) * 18 }}>
              <Spinner className="h-3 w-3" /> loading…
            </span>
          </td>
        </tr>
      )}

      {isExpanded && q.isError && (
        <tr className="border-t border-slate-100">
          <td colSpan={colCount} className="px-3 py-2">
            <span className="text-xs text-red-600" style={{ paddingLeft: (depth + 1) * 18 }}>
              couldn't load children for this node.
            </span>
          </td>
        </tr>
      )}

      {empty && (
        <tr className="border-t border-slate-100">
          <td colSpan={colCount} className="px-3 py-2">
            <span className="text-xs text-slate-500" style={{ paddingLeft: (depth + 1) * 18 }}>
              no ad-level data for this selection — the account may not be backfilled at ad grain yet.
            </span>
          </td>
        </tr>
      )}

      {isExpanded &&
        children.map((child) => (
          <TreeRow
            key={`${child.id ?? '∅'}:${child.currency}`}
            node={child}
            depth={depth + 1}
            nodeKey={`${nodeKey}/${child.dim}:${child.id ?? '∅'}:${child.currency}`}
            ctx={ctx}
          />
        ))}
    </>
  );
}

// The Tree view (Phase 3b): the collapsible Account → Campaign → Ad set → Ad
// drill-down. Fetches the top level (groupBy=account) inheriting the page filters,
// then each node lazily fetches its own children on expand (parent-scoped
// /breakdown). True per-level totals (each node its own authoritative aggregate).
// A filter/date change collapses to the top level (stale children can't linger).
export function BreakdownTree({
  filters,
  visibleCols,
  sort,
  onSortStat,
  onSortDim,
  sortArrow,
  money,
}: {
  filters: ReportingFilters;
  visibleCols: BreakdownColumn[];
  sort: RollupSort;
  onSortStat: (key: BreakdownStatKey) => void;
  onSortDim: () => void;
  sortArrow: (key: RollupSortKey) => string;
  money?: MoneyDisplay;
}) {
  const [expanded, dispatch] = useReducer(expansionReducer, EMPTY_EXPANSION);

  const rangeValid = Boolean(filters.from && filters.to && filters.from <= filters.to);
  const topFilters = useMemo<ReportingBreakdownFilters>(
    () => ({ ...filters, groupBy: ['account'] }),
    [filters],
  );

  // Collapse to the top level whenever the page filters / date range change, so
  // stale children never linger under a changed filter (the top-level query
  // refetches on its own — its key includes the filters).
  const filterSig = JSON.stringify(topFilters);
  useEffect(() => {
    dispatch({ type: 'reset' });
  }, [filterSig]);

  const query = useQuery({
    queryKey: ['reporting-breakdown', topFilters],
    queryFn: () => getReportingBreakdown(topFilters),
    enabled: rangeValid,
    placeholderData: (prev) => prev,
  });

  const topRows = useMemo(
    () => (query.data ? toTreeRows(query.data.rows, 'account', sort) : []),
    [query.data, sort],
  );

  const ctx: TreeContext = {
    filters,
    sort,
    visibleCols,
    expanded,
    toggle: (key) => dispatch({ type: 'toggle', key }),
    money,
  };
  const colCount = 1 + visibleCols.length;

  if (query.isError) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        couldn't load the breakdown for this selection.
      </div>
    );
  }

  return (
    // ⚠️ The max-height is what makes the sticky header work — see the note in
    // GoogleAdsTree. `overflow-x-auto` alone is already a scrollport in both
    // axes, so a sticky header inside it has nothing to stick to.
    <div className="max-h-[75vh] overflow-auto rounded-md border border-slate-200 bg-white scroll-subtle">
      <table className="min-w-full border-collapse text-sm">
        {/* The separating line is an inset shadow, not a border: a collapsed
            border is not painted on a stuck header. */}
        <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 shadow-[inset_0_-1px_0_#e2e8f0]">
          <tr>
            <th className="whitespace-nowrap px-3 py-3 font-medium">
              <button
                type="button"
                onClick={onSortDim}
                className="inline-flex items-center gap-1 hover:text-slate-900"
              >
                Account / Campaign / Ad set / Ad
                <span aria-hidden className="text-[10px]">
                  {sortArrow('name')}
                </span>
              </button>
            </th>
            {visibleCols.map((c) => {
              const stat = isStatKey(c.key);
              return (
                <th
                  key={c.key}
                  className={`whitespace-nowrap px-3 py-3 font-medium ${stat ? 'text-right' : 'text-left'}`}
                >
                  {stat ? (
                    <button
                      type="button"
                      onClick={() => onSortStat(c.key as BreakdownStatKey)}
                      className="inline-flex items-center gap-1 hover:text-slate-900"
                    >
                      {c.label}
                      <span aria-hidden className="text-[10px]">
                        {sortArrow(c.key as BreakdownStatKey)}
                      </span>
                    </button>
                  ) : (
                    c.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {topRows.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="px-4 py-8 text-center text-sm text-slate-500">
                {query.isLoading ? 'loading…' : 'no data for this selection.'}
              </td>
            </tr>
          ) : (
            topRows.map((node) => (
              <TreeRow
                key={`${node.id ?? '∅'}:${node.currency}`}
                node={node}
                depth={0}
                nodeKey={`account:${node.id ?? '∅'}:${node.currency}`}
                ctx={ctx}
              />
            ))
          )}
        </tbody>
      </table>
      {/* The status dot rides the campaign/ad-set/ad name; the Tree always drills
          those levels, so the legend shows whenever there are rows. */}
      {topRows.length > 0 && (
        <div className="px-3 pb-3">
          <StatusDotLegend />
        </div>
      )}
    </div>
  );
}
